import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Store, Action } from '@ngrx/store';
import { AppState } from '../app.state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as actions from './actions';
import { FixtureService } from '../../services/fixture.service';
import { StatusHelper } from '../../helpers/statuses-helper';
import { AlertService } from '../../services/alert.service';
import { switchMap, catchError, map } from 'rxjs/operators';

@Injectable()
export class FixtureRequestEffects {
  constructor(
    protected store: Store<AppState>,
    protected service: FixtureService,
    protected action$: Actions,
    private alertService: AlertService
  ) { }

  GetRequestParticipants$ = createEffect(() => this.action$.pipe(
    ofType<actions.FxRequestGetParticipantsAction>(actions.FXRQ_GET_PARTICIPANTS),
    switchMap(action => this.service.getParticipants().pipe(
      map((result) => new actions.FxRequestGetParticipantsActionSuccess(result)),
      catchError(error => {
        this.alertService.error(error);
        return of(new actions.FxRequestGetParticipantsActionError(error));
      })))));

  GetParticipantsByCompany$ = createEffect(() => this.action$.pipe(
    ofType<actions.FxRequestGetParticipantsByCompanyAction>(actions.FXRQ_GET_PARTICIPANTS_BY_COMPANY),
    switchMap(action => this.service.getParticipantsByCompany(action.companyId).pipe(
      map((result) => new actions.FxRequestGetParticipantsByCompanyActionSuccess(action.companyId, result)),
      catchError(error => {
        this.alertService.error(error);
        return of(new actions.FxRequestGetParticipantsByCompanyActionError(error));
      })))));

  CreateParticipant$ = createEffect(() => this.action$.pipe(
    ofType<actions.FxRequestCreateParticipantAction>(actions.FXRQ_NEW_PARTICIPANT),
    switchMap((action) => this.service.createParticipant(action.item).pipe(
      map(result => new actions.FxRequestCreateParticipantActionSuccess(action.identity, result)),
      catchError(error => {
        this.alertService.error(error);
        return of(new actions.FxRequestCreateParticipantActionError(error));
      })))));

  CreateFixtureRequest$ = createEffect(() => this.action$.pipe(
    ofType<actions.CreateFixtureRequestAction>(actions.CREATE_FIXTURE_REQUEST),
    switchMap(action => this.service.createRequest(action.payload).pipe(
      map((result) => {
        const message = `Fixture Request created, status set to ${StatusHelper.getRequestStatusText(action.payload.status)}`;
        this.alertService.success(message);
        return new actions.CreateFixtureRequestSuccessAction(result);
      }),
      catchError(error => {
        this.alertService.error(error);
        return of(new actions.CreateFixtureRequestErrorAction(error));
      })))));

  UpdateFixtureRequest$ = createEffect(() => this.action$.pipe(
    ofType<actions.UpdateFixtureRequestAction>(actions.UPDATE_FIXTURE_REQUEST),
    switchMap(action => this.service.updateRequest(action.payload).pipe(
      map((result) => {
        const message = `Fixture Request updated, status set to ${StatusHelper.getRequestStatusText(action.payload.status)}`;
        this.alertService.success(message);
        return new actions.UpdateFixtureRequestSuccessAction(result);
      }),
      catchError(error => {
        this.alertService.error(error);
        return of(new actions.UpdateFixtureRequestErrorAction(error));
      })))));

  GetEventsByMatch$ = createEffect(() => this.action$.pipe(
    ofType<actions.GetEventsByMatchAction>(actions.GET_FIXTURE_EVENTS_BYMATCH),
    switchMap(action => this.service.getEventsByMatchId(action.identity.matchId, action.identity.nonLiveInUK).pipe(
      map(events => new actions.GetEventsByMatchActionSuccess({ identity: action.identity, events })),
      catchError(error => {
        this.alertService.error(error);
        return of(new actions.GetEventsByMatchActionError(error));
      })))));
}
